import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog } from "primereact/dialog";
import { addHikvision, addLiveViewCamera, hikvisionLogin } from "../../../../../../Api Methods/Api";
import classes from "../../RuleWizard/RuleWizard.module.css";
import HikvisionStepUsage from "./HikvisionStepUsage";
import HikvisionStepOutput from "./HikvisionStepOutput";
import { HikvisionStepLoggingIn, HikvisionStepLogin } from "./HikvisionStepLogin";
import { ARM_METHODS, buildHikvisionControl, getChannelChoices, getSelectedGroups, includesAlarmInput, normalizeHikvisionEvents, requiresChannels } from "./hikvisionControl";
import HikvisionStepControlTarget from "./HikvisionStepControlTarget";
import HikvisionStepZones from "./HikvisionStepZones";
import HikvisionStepArmMethod from "./HikvisionStepArmMethod";
import HikvisionStepRules from "./HikvisionStepRules";
import HikvisionStepOneKey from "./HikvisionStepOneKey";
import HikvisionStepCameras from "./HikvisionStepCameras";
import HikvisionStepInputs from "./HikvisionStepInputs";
import HikvisionStepLiveView, { STREAM_TYPES, buildHikvisionRtspURL } from "./HikvisionStepLiveView";
import HikvisionStepResult from "./HikvisionStepResult";

// Hikvision setup wizard - laid out like the RuleWizard (step sidebar + content pane), mirroring AiboxForm.
// Flow: Usage -> Login -> Logging in -> [Gates / cameras] -> [Site output if gates + cameras] -> [Zones] -> [Arm/disarm method]
//       -> [Rules | Hikvision outputs] -> [Site output otherwise] -> [Cameras] -> [Live view channels] -> Result
// Bracketed steps only appear when "Control" / "Live view" was picked on the Usage step; Zones onwards only for cameras.
// Gates only: Hikvision outputs -> Site output.
// Zones "Yes" leads to Rules; "No" asks how to arm/disarm - pick rules, or one key disarm (pick Hikvision alarm outputs).
// Cameras is only asked after Rules, and only when the chosen rules are tied to channels (not physical inputs).
// Alarm inputs follows Cameras, and only when the "Alarm Input" rule was chosen - it lists the login's ioInputs.
// Zones "Yes" loops: Rules -> Site output -> [Cameras] -> [Alarm inputs] -> "Link zone" links that zone in the background and returns
// to Rules for the next one, so several site outputs can be linked in one run. "Finish" on Rules ends the loop.
// Control links via addHikvision; live view adds each channel as an RTSP camera via addLiveViewCamera
// (the same way LiveViewCameraForm links a Hikvision NVR).

const HikvisionWizard = ({ setShowVirtualSource, outputs, fetchVirtualSources, liveViewEnabled }) => {
    const dispatch = useDispatch();
    const client = useSelector(state => state.auth.client);
    const token = useSelector(state => state.auth.token);
    const DeviceUUID = useSelector(state => state.auth.DeviceUUID);
    const userID = useSelector(state => state.auth.userID);
    const apiData = { token, client };

    const [currentStep, setCurrentStep] = useState("usage");
    const [completedSteps, setCompletedSteps] = useState(new Set());

    const [selectedOptions, setSelectedOptions] = useState([]);
    const [pinSelected, setPinSelected] = useState(null);
    const [credentials, setCredentials] = useState({ hostName: "", port: "", rtspPort: "", username: "", password: "" });
    const [loginError, setLoginError] = useState("");

    const [events, setEvents] = useState([]);
    const [channels, setChannels] = useState([]);
    const [thermalChannels, setThermalChannels] = useState([]);
    const [ioOutputs, setIOOutputs] = useState([]);
    const [ioInputs, setIOInputs] = useState([]);

    const [selectedControlTargets, setSelectedControlTargets] = useState([]);
    const [wantsZones, setWantsZones] = useState(null);
    const [armMethod, setArmMethod] = useState(null);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [selectedChannels, setSelectedChannels] = useState([]);
    const [selectedOutputIds, setSelectedOutputIds] = useState([]);
    const [selectedInputIds, setSelectedInputIds] = useState([]);
    // Zones mode - the site output for the zone being built, and every zone linked so far this run
    // ({ id, pin, pinName, ruleNames, channelCount, status: "linking" | "linked" | "failed", failReason })
    const [zonePin, setZonePin] = useState(null);
    const [linkedZones, setLinkedZones] = useState([]);
    const [selectedLiveViewChannels, setSelectedLiveViewChannels] = useState([]);
    const [cameraName, setCameraName] = useState("");
    const [streamType, setStreamType] = useState(STREAM_TYPES[0]);

    const [linkStatus, setLinkStatus] = useState("linking");
    const [failReason, setFailReason] = useState("");

    const wantsControl = selectedOptions.includes("Control");
    const wantsLiveView = selectedOptions.includes("Live view");
    const wantsGates = wantsControl && selectedControlTargets.includes("Gates");
    const wantsCameras = wantsControl && selectedControlTargets.includes("Cameras");
    const gatesOnly = wantsGates && !wantsCameras;
    const asksArmMethod = wantsCameras && wantsZones === false;
    // Gates only always controls Hikvision alarm outputs, same as one key disarm
    const usesOneKeyDisarm = gatesOnly || (asksArmMethod && armMethod === ARM_METHODS.ONE_KEY);
    const picksRules = wantsCameras && (wantsZones === true || (asksArmMethod && armMethod === ARM_METHODS.RULES));

    // Cameras are offered based on the chosen rules' groups (thermal vs regular channels)
    const selectedGroups = getSelectedGroups(selectedEvents);
    const channelChoices = getChannelChoices(selectedGroups, channels, thermalChannels);
    const picksCameras = picksRules && requiresChannels(selectedGroups);
    // drop channels left over from rules that have since been deselected
    const validSelectedChannels = selectedChannels.filter(id => channelChoices.some(channel => channel.id === id));
    const picksInputs = picksRules && includesAlarmInput(selectedEvents);

    // The site output is picked once - straight after Gates / cameras when both are chosen,
    // otherwise at the end, after the Hikvision outputs/rules it will control.
    const pickSiteOutputFirst = wantsGates && wantsCameras;

    const zonesMode = wantsCameras && wantsZones === true;
    // The step whose Next links the current zone - Alarm inputs, Cameras, or Site output when the rules need neither
    const zoneFinalStep = picksInputs ? "inputs" : picksCameras ? "cameras" : "zoneOutput";
    // A site output can only be linked once - hide ones already used by a zone (unless that zone failed)
    const zoneOutputs = outputs.filter(output =>
        !(pickSiteOutputFirst && output.Pin === pinSelected)
        && !linkedZones.some(zone => zone.pin === output.Pin && zone.status !== "failed"));

    const stepConfigs = [
        { step: "usage", label: "Usage" },
        { step: "login", label: "Login" },
        { step: "loggingIn", label: "Logging in" },
        wantsControl && { step: "controlTarget", label: "Gates / cameras" },
        pickSiteOutputFirst && { step: "output", label: "Site output" },
        wantsCameras && { step: "zones", label: "Zones" },
        asksArmMethod && { step: "armMethod", label: "Arm/disarm method" },
        picksRules && { step: "rules", label: zonesMode ? "Zone rules" : "Rules" },
        zonesMode && { step: "zoneOutput", label: "Zone site output" },
        usesOneKeyDisarm && { step: "oneKey", label: "Hikvision outputs" },
        wantsControl && !pickSiteOutputFirst && !zonesMode && { step: "output", label: "Site output" },
        picksCameras && { step: "cameras", label: "Cameras" },
        picksInputs && { step: "inputs", label: "Alarm inputs" },
        wantsLiveView && { step: "liveView", label: "Live view channels" },
        { step: "result", label: "Finish" },
    ].filter(Boolean);

    const stepOrder = stepConfigs.map(c => c.step);
    // Last step the user configures before linking - its Next button becomes "Link Hikvision"
    const lastSelectionStep = stepOrder[stepOrder.length - 2];

    const goTo = (step) => {
        setCompletedSteps(prev => new Set(prev).add(currentStep));
        setCurrentStep(step);
    };

    const nextStep = (from) => stepOrder[stepOrder.indexOf(from) + 1];
    // "Logging in" is transient, so going back skips over it to the Login form
    const prevStep = (from) => {
        const prev = stepOrder[stepOrder.indexOf(from) - 1];
        return prev === "loggingIn" ? "login" : prev;
    };

    const HandleClose = () => setShowVirtualSource(null);

    const HandleLogin = async () => {
        setLoginError("");
        goTo("loggingIn");

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15_000);

        try {
            const { rtspPort, ...loginCredentials } = credentials;
            const loginResponse = await hikvisionLogin(apiData, { protocol: "http", ...loginCredentials, userID }, dispatch, controller.signal);
            console.log(loginResponse);
            if (loginResponse?.success) {
                setEvents(normalizeHikvisionEvents(loginResponse.data));
                setChannels(loginResponse.channels ?? []);
                setThermalChannels(loginResponse.thermalChannels ?? []);
                setIOOutputs(loginResponse.ioOutputs ?? []);
                setIOInputs(loginResponse.ioInputs ?? []);
                setCompletedSteps(prev => new Set(prev).add("login").add("loggingIn"));
                setCurrentStep(nextStep("loggingIn"));
            } else {
                setLoginError(loginResponse?.message || "Failed to login to Hikvision, please check your credentials & try again");
                setCurrentStep("login");
            }
        } catch (err) {
            setLoginError(err.name === "AbortError"
                ? "Login timed out. Please check your login credentials and try again."
                : "Failed to login to Hikvision, please check your credentials & try again");
            setCurrentStep("login");
        } finally {
            clearTimeout(timeoutId);
        }
    };

    const HandleLink = async () => {
        goTo("result");
        setLinkStatus("linking");

        try {
            // in zones mode each zone was already linked as it was added
            if (wantsControl && !zonesMode) {
                const body = {
                    pinNumber: pinSelected,
                    siteUUID: DeviceUUID,
                    userID,
                    ipAndPort: `${credentials.hostName}:${credentials.port}`,
                    // TODO: wantsZones isn't sent yet
                    ...buildHikvisionControl({ usesOneKeyDisarm, selectedEvents, selectedChannels: validSelectedChannels, selectedOutputIds, selectedInputIds }),
                };

                console.log(body);

                const linkResponse = await addHikvision(apiData, body, dispatch);
                if (!linkResponse?.success) {
                    setFailReason(linkResponse?.message || "Failed to link Hikvision, please try again.");
                    setLinkStatus("failed");
                    return;
                }
            }

            if (wantsLiveView) {
                const failedChannels = [];
                for (const channel of selectedLiveViewChannels) {
                    const body = {
                        siteUUID: DeviceUUID,
                        cameras: [{
                            name: `${cameraName.trim()} - Cam ${channel}`,
                            rtspURL: buildHikvisionRtspURL(credentials, channel, streamType),
                            type: "HIKVISION",
                            defaultPinned: 0,
                        }],
                    };
                    try {
                        const addResponse = await addLiveViewCamera(apiData, body, dispatch);
                        if (!addResponse?.success) failedChannels.push(channel);
                    } catch (err) {
                        console.error(`Failed to add camera for channel ${channel}`, err);
                        failedChannels.push(channel);
                    }
                }

                if (failedChannels.length > 0) {
                    fetchVirtualSources();
                    setFailReason(`${wantsControl ? "Hikvision control was linked, but the" : "The"} live view camera${failedChannels.length === 1 ? "" : "s"} for channel${failedChannels.length === 1 ? "" : "s"} ${failedChannels.join(", ")} could not be added.`);
                    setLinkStatus("failed");
                    return;
                }
            }

            fetchVirtualSources();

            const failedZones = zonesMode ? linkedZones.filter(zone => zone.status === "failed") : [];
            if (failedZones.length > 0) {
                setFailReason(`${failedZones.length} zone${failedZones.length === 1 ? "" : "s"} could not be linked (${failedZones.map(zone => zone.pinName).join(", ")}). The other zones were linked.`);
                setLinkStatus("failed");
                return;
            }

            setLinkStatus("success");
        } catch {
            setFailReason("An unexpected error occurred. Please try again.");
            setLinkStatus("failed");
        }
    };

    // Zones mode - links the zone being built in the background, then resets for the next zone
    // and returns to Rules without closing the wizard.
    const LinkZone = () => {
        const zoneId = crypto.randomUUID();
        const body = {
            pinNumber: zonePin,
            siteUUID: DeviceUUID,
            userID,
            ipAndPort: `${credentials.hostName}:${credentials.port}`,
            // TODO: nothing marks this as a zone for the backend yet
            ...buildHikvisionControl({ usesOneKeyDisarm: false, selectedEvents, selectedChannels: validSelectedChannels, selectedInputIds }),
        };

        console.log(body);

        setLinkedZones(prev => [...prev, {
            id: zoneId,
            pin: zonePin,
            pinName: outputs.find(output => output.Pin === zonePin)?.pinName ?? `Output ${zonePin}`,
            ruleNames: selectedEvents.map(event => event.displayName),
            channelCount: body.channelIds?.length ?? 0,
            status: "linking",
        }]);

        const setZoneResult = (status, failReason) =>
            setLinkedZones(prev => prev.map(zone => zone.id === zoneId ? { ...zone, status, failReason } : zone));

        addHikvision(apiData, body, dispatch)
            .then(response => {
                if (response?.success) {
                    setZoneResult("linked");
                    fetchVirtualSources();
                } else {
                    setZoneResult("failed", response?.message || "Failed to link");
                }
            })
            .catch(() => setZoneResult("failed", "An unexpected error occurred"));

        setSelectedEvents([]);
        setSelectedChannels([]);
        setSelectedInputIds([]);
        setZonePin(null);
        goTo("rules");
    };

    const FinishZones = () => wantsLiveView ? goTo("liveView") : HandleLink();

    const advanceFrom = (step) => {
        if (zonesMode && step === zoneFinalStep) return LinkZone();
        return step === lastSelectionStep ? HandleLink() : goTo(nextStep(step));
    };

    const getStepSummary = (step) => {
        switch (step) {
            case "usage":
                return selectedOptions.join(" + ") || null;
            case "output":
                return outputs.find(o => o.Pin === pinSelected)?.pinName ?? null;
            case "login":
                return credentials.hostName || null;
            case "loggingIn":
                return "Connected";
            case "controlTarget":
                return selectedControlTargets.join(" + ") || null;
            case "cameras":
                return `${validSelectedChannels.length} camera${validSelectedChannels.length === 1 ? "" : "s"}`;
            case "inputs":
                return `${selectedInputIds.length} input${selectedInputIds.length === 1 ? "" : "s"}`;
            case "zones":
                return wantsZones == null ? null : (wantsZones ? "Yes" : "No");
            case "armMethod":
                return armMethod === ARM_METHODS.ONE_KEY ? "One key disarm" : armMethod === ARM_METHODS.RULES ? "Pick rules" : null;
            case "oneKey":
                return `${selectedOutputIds.length} output${selectedOutputIds.length === 1 ? "" : "s"}`;
            case "rules":
                if (zonesMode) {
                    const linkedCount = linkedZones.filter(zone => zone.status === "linked").length;
                    return `${linkedCount} zone${linkedCount === 1 ? "" : "s"} linked`;
                }
                return `${selectedEvents.length} rule${selectedEvents.length === 1 ? "" : "s"}`;
            case "zoneOutput":
                return outputs.find(o => o.Pin === zonePin)?.pinName ?? null;
            case "liveView":
                return `${selectedLiveViewChannels.length} channel${selectedLiveViewChannels.length === 1 ? "" : "s"}`;
            default:
                return null;
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case "usage":
                return (
                    <HikvisionStepUsage
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        onNext={() => goTo(nextStep("usage"))}
                        liveViewEnabled={liveViewEnabled}
                    />
                );
            case "output":
                return (
                    <HikvisionStepOutput
                        outputs={outputs}
                        pinSelected={pinSelected}
                        setPinSelected={setPinSelected}
                        isLastStep={lastSelectionStep === "output"}
                        onNext={() => advanceFrom("output")}
                        onBack={() => setCurrentStep(prevStep("output"))}
                    />
                );
            case "login":
                return (
                    <HikvisionStepLogin
                        credentials={credentials}
                        setCredentials={setCredentials}
                        loginError={loginError}
                        showRtspPort={wantsLiveView}
                        onLogin={HandleLogin}
                        onBack={() => setCurrentStep(prevStep("login"))}
                    />
                );
            case "loggingIn":
                return <HikvisionStepLoggingIn onClose={HandleClose} />;
            case "controlTarget":
                return (
                    <HikvisionStepControlTarget
                        selectedControlTargets={selectedControlTargets}
                        setSelectedControlTargets={setSelectedControlTargets}
                        onNext={() => goTo(nextStep("controlTarget"))}
                        onBack={() => setCurrentStep(prevStep("controlTarget"))}
                    />
                );
            case "cameras":
                return (
                    <HikvisionStepCameras
                        channelChoices={channelChoices}
                        selectedChannels={validSelectedChannels}
                        setSelectedChannels={setSelectedChannels}
                        isLastStep={zonesMode ? zoneFinalStep === "cameras" : lastSelectionStep === "cameras"}
                        lastStepLabel={zonesMode ? "Link zone" : undefined}
                        onNext={() => advanceFrom("cameras")}
                        onBack={() => setCurrentStep(prevStep("cameras"))}
                    />
                );
            case "inputs":
                return (
                    <HikvisionStepInputs
                        ioInputs={ioInputs}
                        selectedInputIds={selectedInputIds}
                        setSelectedInputIds={setSelectedInputIds}
                        isLastStep={zonesMode || lastSelectionStep === "inputs"}
                        lastStepLabel={zonesMode ? "Link zone" : undefined}
                        onNext={() => advanceFrom("inputs")}
                        onBack={() => setCurrentStep(prevStep("inputs"))}
                    />
                );
            case "zones":
                return (
                    <HikvisionStepZones
                        wantsZones={wantsZones}
                        setWantsZones={setWantsZones}
                        isLastStep={lastSelectionStep === "zones"}
                        onNext={() => advanceFrom("zones")}
                        onBack={() => setCurrentStep(prevStep("zones"))}
                    />
                );
            case "armMethod":
                return (
                    <HikvisionStepArmMethod
                        armMethod={armMethod}
                        setArmMethod={setArmMethod}
                        hasOutputs={ioOutputs.length > 0}
                        isLastStep={lastSelectionStep === "armMethod"}
                        onNext={() => advanceFrom("armMethod")}
                        onBack={() => setCurrentStep(prevStep("armMethod"))}
                    />
                );
            case "rules":
                return (
                    <HikvisionStepRules
                        events={events}
                        selectedEvents={selectedEvents}
                        setSelectedEvents={setSelectedEvents}
                        isLastStep={lastSelectionStep === "rules"}
                        linkedZones={zonesMode ? linkedZones : undefined}
                        onFinish={zonesMode ? FinishZones : undefined}
                        onNext={() => advanceFrom("rules")}
                        onBack={() => setCurrentStep(prevStep("rules"))}
                    />
                );
            case "zoneOutput":
                return (
                    <HikvisionStepOutput
                        outputs={zoneOutputs}
                        pinSelected={zonePin}
                        setPinSelected={setZonePin}
                        isLastStep={zoneFinalStep === "zoneOutput"}
                        lastStepLabel="Link zone"
                        onNext={() => advanceFrom("zoneOutput")}
                        onBack={() => setCurrentStep(prevStep("zoneOutput"))}
                    />
                );
            case "oneKey":
                return (
                    <HikvisionStepOneKey
                        ioOutputs={ioOutputs}
                        selectedOutputIds={selectedOutputIds}
                        setSelectedOutputIds={setSelectedOutputIds}
                        isLastStep={lastSelectionStep === "oneKey"}
                        onNext={() => advanceFrom("oneKey")}
                        onBack={() => setCurrentStep(prevStep("oneKey"))}
                    />
                );
            case "liveView":
                return (
                    <HikvisionStepLiveView
                        // Hikvision channels are keyed by `id`, the live view step expects `no`
                        channels={channels.map(channel => ({ ...channel, no: String(channel.id) }))}
                        selectedLiveViewChannels={selectedLiveViewChannels}
                        setSelectedLiveViewChannels={setSelectedLiveViewChannels}
                        cameraName={cameraName}
                        setCameraName={setCameraName}
                        streamType={streamType}
                        setStreamType={setStreamType}
                        isLastStep={lastSelectionStep === "liveView"}
                        onNext={() => advanceFrom("liveView")}
                        // in zones mode the zone steps were reset after linking, so go back to the zone list
                        onBack={() => setCurrentStep(zonesMode ? "rules" : prevStep("liveView"))}
                    />
                );
            case "result":
                return (
                    <HikvisionStepResult
                        status={linkStatus}
                        failReason={failReason}
                        onBack={() => setCurrentStep(zonesMode ? "rules" : lastSelectionStep)}
                        onClose={HandleClose}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Dialog
            visible
            onHide={HandleClose}
            style={{ width: "900px" }}
            header="Link Hikvision"
            modal
            pt={{ content: { style: { padding: 0 } } }}
        >
            <div className={classes.wizardContainer}>
                <div className={classes.sidebar}>
                    <span className={classes.sidebarTitle}>Hikvision Setup</span>
                    {stepConfigs.map((config, i) => {
                        const isActive = config.step === currentStep;
                        const isDone = completedSteps.has(config.step) && !isActive;
                        const summary = isDone ? getStepSummary(config.step) : null;
                        return (
                            <div
                                key={config.step}
                                className={`${classes.stepItem} ${isActive ? classes.stepItemActive : ""}`}
                            >
                                <div
                                    className={`${classes.stepNumber} ${
                                        isActive ? classes.stepNumberActive : isDone ? classes.stepNumberDone : ""
                                    }`}
                                >
                                    {isDone ? "✓" : i + 1}
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                                    <span
                                        className={`${classes.stepLabel} ${
                                            isActive ? classes.stepLabelActive : isDone ? classes.stepLabelDone : ""
                                        }`}
                                    >
                                        {config.label}
                                    </span>
                                    {summary && <span className={classes.stepSummary}>{summary}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className={classes.content}>
                    {renderStep()}
                </div>
            </div>
        </Dialog>
    );
};

export default HikvisionWizard;
